import CitationTable from "@/components/charts/CitationTable";
import ContentCitationTable from "@/components/charts/ContentCitationTable";
import MobileTooltip from "@/components/common/MobileTooltip";
import { formatPercent } from "@/utils/format";
import { Grid } from "antd";
import * as echarts from "echarts";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

interface TrendPoint {
  day: string;
  mentions: number; // 提及量/提及率（百分比数据亦可）
}

interface CloudWord {
  text: string;
  weight: number;
}

interface CitationAnalysisSectionProps {
  brandName: string;
  title?: string;
  trend?: TrendPoint[];
  highFreqWords?: CloudWord[];
  lowFreqWords?: CloudWord[];
  brandId?: string;
  categoryId?: string;
  days?: number;
  modelId?: string;
  mediaRows?: any[];
  contentRows?: any[];
}

const defaultTrend: TrendPoint[] = [
  { day: "8月31日", mentions: 12 },
  { day: "9月1日", mentions: 18 },
  { day: "9月2日", mentions: 15 },
  { day: "9月3日", mentions: 22 },
  { day: "9月4日", mentions: 17 },
  { day: "9月5日", mentions: 19 },
  { day: "9月6日", mentions: 25 },
];

const defaultHigh: CloudWord[] = [
  { text: "价格", weight: 40 },
  { text: "性能", weight: 38 },
  { text: "续航", weight: 35 },
  { text: "做工", weight: 30 },
  { text: "口碑", weight: 28 },
  { text: "配置", weight: 26 },
];

const defaultLow: CloudWord[] = [
  { text: "包装", weight: 20 },
  { text: "物流", weight: 18 },
  { text: "细节", weight: 16 },
  { text: "颜色", weight: 15 },
  { text: "附件", weight: 14 },
];

const CitationAnalysisSection: React.FC<CitationAnalysisSectionProps> = ({
  brandName,
  title = "Citation analysis",
  trend = defaultTrend,
  highFreqWords = defaultHigh,
  lowFreqWords = defaultLow,
  brandId,
  categoryId,
  days = 7,
  modelId = "all",
  mediaRows,
  contentRows,
}) => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const lineRef = useRef<HTMLDivElement>(null);
  const lineInst = useRef<echarts.ECharts | null>(null);
  const posCloudRef = useRef<HTMLDivElement>(null);
  const negCloudRef = useRef<HTMLDivElement>(null);
  const posCloudInst = useRef<echarts.ECharts | null>(null);
  const negCloudInst = useRef<echarts.ECharts | null>(null);

  // 初始化
  useEffect(() => {
    if (lineRef.current && !lineInst.current)
      lineInst.current = echarts.init(lineRef.current);
    if (posCloudRef.current && !posCloudInst.current)
      posCloudInst.current = echarts.init(posCloudRef.current);
    if (negCloudRef.current && !negCloudInst.current)
      negCloudInst.current = echarts.init(negCloudRef.current);
    const onResize = () => {
      lineInst.current?.resize();
      posCloudInst.current?.resize();
      negCloudInst.current?.resize();
      // setIsWide(window.innerWidth >= 1024)
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      lineInst.current?.dispose();
      lineInst.current = null;
      posCloudInst.current?.dispose();
      posCloudInst.current = null;
      negCloudInst.current?.dispose();
      negCloudInst.current = null;
    };
  }, []);

  // 线图配置
  useEffect(() => {
    if (!lineInst.current) return;
    const option = {
      title: {
        text: `${title} - 提及趋势（${brandName}）`,
        left: "left",
        textStyle: { fontSize: 16, fontWeight: "normal" },
      },
      tooltip: { trigger: "axis" },
      grid: { left: "3%", right: "4%", bottom: "10%", containLabel: true },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: trend.map((t) => t.day),
      },
      yAxis: {
        type: "value",
        axisLabel: { formatter: (val: number) => formatPercent(val, 1) },
        min: 0,
        max: 60,
      },
      series: [
        {
          name: t("api.metric_mention_rate"),
          type: "line",
          smooth: true,
          data: trend.map((t) => t.mentions),
          lineStyle: { width: 3, color: "#21C4B8" },
          itemStyle: { color: "#21C4B8" },
        },
      ],
    };
    lineInst.current.setOption(option, true);
  }, [brandName, title, JSON.stringify(trend)]);

  // 词云配置
  useEffect(() => {
    const toCloud = (arr: CloudWord[]) =>
      arr.map((k) => ({ name: k.text, value: k.weight }));
    const base = {
      type: "wordCloud",
      gridSize: 8,
      sizeRange: [12, 48],
      rotationRange: [0, 0],
      shape: "circle",
      textStyle: {
        color: () =>
          [
            "#3b82f6",
            "#4BA67A",
            "#ef4444",
            "#f59e0b",
            "#8b5cf6",
            "#21C4B8",
            "#4BA67A",
          ][Math.floor(Math.random() * 7)],
      },
    } as any;
    posCloudInst.current?.setOption(
      {
        title: { text: t("search_detail.citation_high_freq"), left: "left" },
        series: [{ ...base, data: toCloud(highFreqWords) }],
      },
      true
    );
    negCloudInst.current?.setOption(
      {
        title: { text: t("search_detail.citation_low_freq"), left: "left" },
        series: [{ ...base, data: toCloud(lowFreqWords) }],
      },
      true
    );
  }, [JSON.stringify(highFreqWords), JSON.stringify(lowFreqWords)]);

  const rootRef = React.useRef<HTMLDivElement>(null);
  const [tipMaxWidth, setTipMaxWidth] = React.useState<number>(360);
  const tipAnchorRef = React.useRef<HTMLDivElement>(null);
  const [tipOpen, setTipOpen] = React.useState<boolean>(false);
  React.useLayoutEffect(() => {
    const calc = () => {
      const container = rootRef.current;
      const anchor = tipAnchorRef.current;
      if (!container || !anchor) return;
      const cr = container.getBoundingClientRect();
      const ar = anchor.getBoundingClientRect();
      const cs = window.getComputedStyle(container);
      const pr = parseFloat(cs.paddingRight || "0");
      const br = parseFloat(cs.borderRightWidth || "0");
      const sectionRight = cr.right - pr - br;
      const viewportRight = window.innerWidth - 8;
      const rightEdge = Math.min(sectionRight, viewportRight);
      const available = Math.max(0, Math.floor(rightEdge - ar.left - 4));
      setTipMaxWidth(available);
    };
    const ro = new ResizeObserver(calc);
    if (rootRef.current) ro.observe(rootRef.current);
    calc();
    window.addEventListener("scroll", calc, { passive: true });
    window.addEventListener("resize", calc);

    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", calc as any);
      window.removeEventListener("resize", calc as any);
    };
  }, []);
  React.useEffect(() => {
    const close = () => setTipOpen(false);
    const container = rootRef.current;
    window.addEventListener("scroll", close, { passive: true });
    container?.addEventListener("scroll", close, { passive: true } as any);
    return () => {
      window.removeEventListener("scroll", close as any);
      container?.removeEventListener("scroll", close as any);
    };
  }, []);
  return (
    <div
      ref={rootRef}
      style={{
        marginTop: 16,
        background: "#ffffff",
        borderRadius: 6,
        padding: isMobile ? 10 : 20,
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 2px 8px -2px rgba(0,0,0,.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <span style={{ color: "#000000", fontSize: 18, fontWeight: "normal" }}>
          {t("search_detail.citation_source_dist")}
        </span>
        <MobileTooltip
          content={
            <>
              {t("api.citation_media_tooltip")}
              <br />
              <br />
              {t("api.citation_content_tooltip")}
            </>
          }
          title={
            t("search_detail.citation_source_dist") +
            " - " +
            t("search_detail.insight_metric_desc")
          }
        />
      </div>
      {/* 表格容器：参考CompetitorOverviewSection的单列全宽布局 */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* 媒体引用分析表（父级传入数据，组件不再自拉取） */}
        <div style={{ width: "100%" }}>
          <CitationTable
            brandId={brandId}
            categoryId={categoryId}
            days={days}
            modelId={modelId}
            rows={mediaRows}
          />
        </div>

        {/* 内容引用分析表 */}
        <div style={{ width: "100%" }}>
          <ContentCitationTable rows={contentRows} />
        </div>
      </div>

      {/* 问题列表已按需求从本 Section 移除 */}
    </div>
  );
};

export default CitationAnalysisSection;
